import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { AdapterContext, arrayIsNullOrEmpty, ITridentSparkJobDefinitionActivity, ModelConstants, ReferenceAdapter, stringIsNullOrEmpty, TridentSparkJobDefinitionActivity, TridentSparkJobDefinitionLinkedService } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { IFabricSparkJobDefinitionActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { CopilotNameMapperService } from 'app/services/copilot-name-mapper.service';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import { GlobalVariables } from 'app/services/globals';
import { getFabricLsForParameterizedConnections } from '../utils';
import { toParameterClass, toParameterObject } from '../linkedservices/parameter-config.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(TridentSparkJobDefinitionActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: async (context: AdapterContext) => {
            const exposureControlService = await getServiceInstance<ExposureControlService>(
                context.contextData,
                'ExposureControlService'
            );
            return exposureControlService.UseTokenServiceInSJDActivity ? linkedServiceReferenceWithoutParameterizationRemainer : undefined;
        },
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            sparkJobDefinitionId: '*',
            workspaceId: '*',
            executableFile: '*',
            mainClass: '*',
            commandLineArguments: '*',
            additionalLibraryUris: '*',
            defaultLakehouse: '*',
            additionalLakehouses: '*',
            environmentId: '*',
            driverCores: '*',
            driverMemory: '*',
            executorCores: '*',
            executorMemory: '*',
            dynamicExecutorAllocation: '*',
            numExecutors: '*',
            minExecutors: '*',
            maxExecutors: '*',
            sparkProperties: '*',
            environment: async (context: AdapterContext) => {
                const exposureControlService = await getServiceInstance<ExposureControlService>(
                    context.contextData,
                    'ExposureControlService'
                );
                return exposureControlService.enableSJDEnvCrossWorkspace ? '*' : undefined;
            }
        },
        externalReferences:'*'
    }
})
export class TridentSparkJobDefinitionActivityAdapter extends ActivityAsyncAdapter<TridentSparkJobDefinitionActivity, ITridentSparkJobDefinitionActivity, IFabricSparkJobDefinitionActivityContext> {
    public override async toFabricCopilotContext(activity: TridentSparkJobDefinitionActivity): Promise<IFabricSparkJobDefinitionActivityContext> {
        if (activity) {
            const activityContext: IFabricSparkJobDefinitionActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.JobDefinitionId = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARKJOBDEFINITIONID, true);
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.WORKSPACEID, true);
            const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
                this.context.contextData,
                'CopilotNameMapperService'
            );
            const [sparkJobName, sparkWorkspaceName] = await Promise.all([
                nameMapperService.getTridentArtifactNameById('sparkjobdefinition', activityContext.JobDefinitionId),
                nameMapperService.getTridentArtifactNameById('workspace', activityContext.WorkspaceId)
            ]);
            activityContext.JobDefinitionName = sparkJobName;
            activityContext.WorkspaceName = sparkWorkspaceName;
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IFabricSparkJobDefinitionActivityContext,
        model?: TridentSparkJobDefinitionActivity
    ): Promise<TridentSparkJobDefinitionActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, TridentSparkJobDefinitionActivity.SPARKJOBDEFINITIONID, context.JobDefinitionId);
            ValueAdapter.toModel(model, TridentSparkJobDefinitionActivity.WORKSPACEID, context.WorkspaceId);
        }
        return model;
    }

    public async postToModel(activity: TridentSparkJobDefinitionActivity): Promise<void> {
        await super.postToModel(activity);
        if (!__IS_FABRIC_DPE__) {
            return;
        }
        const nameMapperService = await getServiceInstance<CopilotNameMapperService>(
            this.context.contextData,
            'CopilotNameMapperService'
        );
        const sparkJobDefinitionId = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARKJOBDEFINITIONID, true);
        const workspaceId = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.WORKSPACEID, true);

        if (sparkJobDefinitionId && !isLogicAppExpression(sparkJobDefinitionId)) {
            nameMapperService.getTridentArtifactNameById('sparkjobdefinition', sparkJobDefinitionId);
        }

        if (workspaceId && !isLogicAppExpression(workspaceId)) {
            nameMapperService.getTridentArtifactNameById('workspace', workspaceId);
        }
    }

    protected async _toResource(activity: TridentSparkJobDefinitionActivity): Promise<ITridentSparkJobDefinitionActivity> {
        const modelTridentSparkJobDefinitionActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(TridentSparkJobDefinitionActivity).name) as ITridentSparkJobDefinitionActivity;

        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        if (exposureControlService.UseTokenServiceInSJDActivity && activity.tridentSparkJobDefinitionLinkedService) {
            modelTridentSparkJobDefinitionActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.tridentSparkJobDefinitionLinkedService,
                activity.linkedServiceParameters);
        }

        modelTridentSparkJobDefinitionActivity.typeProperties = {
            sparkJobDefinitionId: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARKJOBDEFINITIONID),
            workspaceId: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.WORKSPACEID),
            driverCores: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARK_DRIVER_CORE),
            driverMemory: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARK_DRIVER_MEMORY),
            executorCores: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARK_EXECUTOR_CORE),
            executorMemory: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARK_EXECUTOR_MEMORY),
            dynamicExecutorAllocation: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.DYNAMIC_ALLOCATION),
            numExecutors: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.SPARK_EXECUTOR_INSTANCES),
            minExecutors: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.MIN_EXECUTORS),
            maxExecutors: ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.MAX_EXECUTORS),
            sparkProperties: toParameterObject(activity.sparkProperties, true)
        };

        if (!stringIsNullOrEmpty(activity.executableFile?.trim()) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.EXECUTABLEFILE.templateValue(activity))) {
            modelTridentSparkJobDefinitionActivity.typeProperties.executableFile = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.EXECUTABLEFILE);
        }
        if (!stringIsNullOrEmpty(activity.mainClass?.trim()) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.MAINCLASS.templateValue(activity))) {
            modelTridentSparkJobDefinitionActivity.typeProperties.mainClass = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.MAINCLASS);
        }
        if (!stringIsNullOrEmpty(activity.commandLineArguments?.trim()) || TridentSparkJobDefinitionActivity.COMMANDLINEARGUMENTS.templateValue(activity) != undefined) {
            modelTridentSparkJobDefinitionActivity.typeProperties.commandLineArguments = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.COMMANDLINEARGUMENTS);
        }
        if (!stringIsNullOrEmpty(activity.additionalLibraryUris?.trim()) || TridentSparkJobDefinitionActivity.ADDITIONALLIBRARYURIS.templateValue(activity) != undefined) {
            modelTridentSparkJobDefinitionActivity.typeProperties.additionalLibraryUris = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.ADDITIONALLIBRARYURIS);
        }
        if ((!stringIsNullOrEmpty(activity.defaultLakehouseWorkspaceId) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEWID.templateValue(activity))) &&
            (!stringIsNullOrEmpty(activity.defaultLakehouseArtifactId) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEAID.templateValue(activity)))) {
            modelTridentSparkJobDefinitionActivity.typeProperties.defaultLakehouse = {
                'workspaceId': ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEWID),
                'artifactId': ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEAID)
            };
        }
        if (exposureControlService.enableSJDEnvCrossWorkspace) {
            if (!stringIsNullOrEmpty(activity.environmentId) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.ENVIRONMENTID.templateValue(activity))) {
                // If old environmentId is already set, set the environment object with current workspaceId and already set environment artifactId
                modelTridentSparkJobDefinitionActivity.typeProperties.environment = {
                    'workspaceId': ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.WORKSPACEID),
                    'artifactId': ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTID)
                };

                // Clear the environmentId as it is now part of the environment object
                // This is to ensure backward compatibility with old SJD activities that only had environmentId
                activity.environmentId = undefined;
                TridentSparkJobDefinitionActivity.ENVIRONMENTID.setTemplateValue(activity, undefined);
            }
            else {
                if ((!stringIsNullOrEmpty(activity.environmentWorkspaceId) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.ENVIRONMENTWID.templateValue(activity))) &&
                    (!stringIsNullOrEmpty(activity.environmentArtifactId) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.ENVIRONMENTAID.templateValue(activity)))) {
                    modelTridentSparkJobDefinitionActivity.typeProperties.environment = {
                        'workspaceId': ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTWID),
                        'artifactId': ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTAID)
                    };
                }
            }
        }
        else {
            if (!stringIsNullOrEmpty(activity.environmentId) || !stringIsNullOrEmpty(TridentSparkJobDefinitionActivity.ENVIRONMENTID.templateValue(activity))) {
                modelTridentSparkJobDefinitionActivity.typeProperties.environmentId = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTID);
            }
        }
        if (!arrayIsNullOrEmpty(activity.additionalLakehouses) || TridentSparkJobDefinitionActivity.ADDITIONALLAKEHOUSES.templateValue(activity) != undefined) {
            modelTridentSparkJobDefinitionActivity.typeProperties.additionalLakehouses = ValueAdapter.toResource(activity, TridentSparkJobDefinitionActivity.ADDITIONALLAKEHOUSES);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelTridentSparkJobDefinitionActivity, parsed);
        }

        return modelTridentSparkJobDefinitionActivity;
    }

    protected async _toModel(modelActivity: ITridentSparkJobDefinitionActivity): Promise<TridentSparkJobDefinitionActivity> {
        const activity: TridentSparkJobDefinitionActivity = TridentSparkJobDefinitionActivity.MODEL.create<TridentSparkJobDefinitionActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        const exposureControlService = await getServiceInstance<ExposureControlService>(
            this.context.contextData,
            'ExposureControlService'
        );

        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.SPARKJOBDEFINITIONID, modelActivity.typeProperties.sparkJobDefinitionId);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.WORKSPACEID, modelActivity.typeProperties.workspaceId);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.SPARK_DRIVER_CORE, modelActivity.typeProperties.driverCores);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.SPARK_DRIVER_MEMORY, modelActivity.typeProperties.driverMemory);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.SPARK_EXECUTOR_CORE, modelActivity.typeProperties.executorCores);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.SPARK_EXECUTOR_MEMORY, modelActivity.typeProperties.executorMemory);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.DYNAMIC_ALLOCATION, modelActivity.typeProperties.dynamicExecutorAllocation);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.SPARK_EXECUTOR_INSTANCES, modelActivity.typeProperties.numExecutors);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.MIN_EXECUTORS, modelActivity.typeProperties.minExecutors);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.MAX_EXECUTORS, modelActivity.typeProperties.maxExecutors);
        activity.sparkProperties = toParameterClass(modelActivity.typeProperties.sparkProperties, activity, true);

        if (exposureControlService.UseTokenServiceInSJDActivity && modelActivity.linkedServiceName != undefined) {
            activity.tridentSparkJobDefinitionLinkedService = getFabricLsForParameterizedConnections(
                        modelActivity.linkedServiceName,
                        ModelConstants.sparkJobDefinitionLinkedService, 
                        activity);
            
            if(activity.tridentSparkJobDefinitionLinkedService === undefined) {

                activity.tridentSparkJobDefinitionLinkedService = ReferenceAdapter.toProxyModel<TridentSparkJobDefinitionLinkedService>(Metamodel.modelFor(TridentSparkJobDefinitionLinkedService),
                modelActivity.linkedServiceName,
                this.context);                
            }

            if (GlobalVariables.enforceConnectionForNewActivity) {
                activity.isConnectionRequired = true;
            }
        }

        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.EXECUTABLEFILE, modelActivity.typeProperties.executableFile);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.MAINCLASS, modelActivity.typeProperties.mainClass);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.COMMANDLINEARGUMENTS, modelActivity.typeProperties.commandLineArguments);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ADDITIONALLIBRARYURIS, modelActivity.typeProperties.additionalLibraryUris);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEWID, modelActivity.typeProperties.defaultLakehouse?.workspaceId);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.DEFAULTLAKEHOUSEAID, modelActivity.typeProperties.defaultLakehouse?.artifactId);
        ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ADDITIONALLAKEHOUSES, modelActivity.typeProperties.additionalLakehouses);
        if (exposureControlService.enableSJDEnvCrossWorkspace) {
            if (modelActivity.typeProperties.environmentId != undefined) {
                ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTWID, modelActivity.typeProperties.workspaceId);
                ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTAID, modelActivity.typeProperties.environmentId);
                modelActivity.typeProperties.environmentId = undefined; // Clear the environmentId as it is now part of the environment object
            }
            else {
                ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTWID, modelActivity.typeProperties.environment?.workspaceId);
                ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTAID, modelActivity.typeProperties.environment?.artifactId);
            }
        }
        else {
            ValueAdapter.toModel(activity, TridentSparkJobDefinitionActivity.ENVIRONMENTID, modelActivity.typeProperties.environmentId);
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, TridentSparkJobDefinitionActivity.MODEL));
        return activity;
    }
}
